import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaAward, FaGraduationCap, FaUsers, FaHandshake } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import TabNav from '../components/TabNav';
import Card from '../components/Card';
import Bureau from '../components/Bureau';
import ScrollReveal, { StaggerReveal, StaggerItem } from '../components/ScrollReveal';
import { historyEvents } from '../data/Events';
import { FIXED_H } from '../components/layoutConstants';

// ── Définition des sections ──────────────────────────────────
const tabDefs = [
  { key: 'histoire',  label: 'Histoire',  eyebrow: 'Depuis 1998',         title: 'Notre Histoire'       },
  { key: 'culture',   label: 'Culture',   eyebrow: 'Ce qui nous définit', title: 'Notre Culture'        },
  { key: 'bureau',    label: 'Bureau',    eyebrow: "L'équipe dirigeante", title: 'Le Bureau'            },
  { key: 'gymnase',   label: 'Gymnase',   eyebrow: 'Nos installations',   title: 'Le Gymnase'           },
  { key: 'reglement', label: 'Règlement', eyebrow: 'Vie du club',         title: 'Règlement Intérieur'  },
];

// ── Composants de section ────────────────────────────────────
const statusStyle = { Passé: 'bg-stone-100 text-stone-500', default: 'bg-green-50 text-green-700' };

const TimelineEvent = ({ date, title, description, status }) => (
  <StaggerItem>
    <div className={`flex gap-5 pb-8 ${status === 'Passé' ? 'opacity-40' : ''}`}>
      <div className="flex flex-col items-center shrink-0 pt-1">
        <div className={`w-3 h-3 rounded-full ${status === 'Passé' ? 'bg-stone-300' : 'bg-brand-800'}`} />
        <div className="w-px flex-1 mt-1 bg-stone-100" />
      </div>
      <div className="card-minimal p-5 flex-1 -mt-0.5">
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <p className="font-semibold text-sm text-brand-800">{date}</p>
          <span className={`tag ${statusStyle[status] || statusStyle.default}`}>{status}</span>
        </div>
        <h4 className="font-semibold text-stone-950 text-sm mb-1">{title}</h4>
        {description && <p className="text-xs text-stone-500 leading-relaxed">{description}</p>}
      </div>
    </div>
  </StaggerItem>
);

const reglementArticles = [
  { title: 'Article 1 : Objet', text: "Le présent règlement intérieur précise les règles de fonctionnement du Club de Badminton d'Éragny. Tous les membres s'engagent à le respecter." },
  { title: 'Article 2 : Adhésion', items: ['Cotisation annuelle fixée chaque année par le bureau.', 'Licence fédérale en cours de validité obligatoire.', "Acceptation des statuts et du règlement intérieur."] },
  { title: 'Article 3 : Comportement', items: ['Attitude respectueuse envers tous les membres.', 'Fair-play en toutes circonstances.', 'Respect des consignes de sécurité.'] },
  { title: 'Article 4 : Installations', items: ["Accès aux terrains uniquement aux heures définies.", 'Prise en charge du matériel mis à disposition.', 'Propreté des installations après utilisation.'] },
  { title: 'Article 5 : Entraînements', items: ['Ponctualité et régularité requises.', 'Respect des engagements de compétition.', 'Tenue adaptée obligatoire.'] },
  { title: 'Article 6 : Sanctions', items: ["Sanctions allant de l'avertissement à l'exclusion.", 'Procédure disciplinaire en cas de faute grave.'] },
  { title: 'Article 7 : Modification', text: "Le règlement peut être modifié par décision du bureau. Les membres seront informés par voie d'affichage et sur le site." },
];

const SectionHistoire  = () => (
  <section className="section bg-white">
    <div className="max-w-6xl mx-auto">
      <ScrollReveal className="mb-10">
        <span className="eyebrow mb-3 block">Depuis 1998</span>
        <h2 className="display-md">Notre Histoire</h2>
      </ScrollReveal>
      <div className="max-w-2xl">
        <StaggerReveal>
          {historyEvents.map((e, i) => <TimelineEvent key={i} {...e} />)}
        </StaggerReveal>
      </div>
    </div>
  </section>
);

const SectionCulture = () => (
  <section className="section bg-stone-50">
    <div className="max-w-6xl mx-auto">
      <ScrollReveal className="mb-10">
        <span className="eyebrow mb-3 block">Ce qui nous définit</span>
        <h2 className="display-md">Notre Culture</h2>
      </ScrollReveal>
      <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { title: 'Nos Valeurs',     content: "Excellence, esprit d'équipe, respect et innovation — nos valeurs guident chacune de nos actions.", icon: FaAward,         buttonText: 'En savoir plus'    },
          { title: 'Convivialité',    content: "Un environnement bienveillant où chacun peut s'épanouir dans la pratique du badminton.",           icon: FaUsers,        buttonText: 'Notre philosophie' },
          { title: 'Formation',       content: 'Programme adapté de 8 à 18 ans, encadré par des entraîneurs qualifiés.',                           icon: FaGraduationCap,buttonText: 'Découvrir'         },
          { title: "Esprit d'équipe", content: 'Entraide et soutien mutuel : chaque membre compte, du débutant au confirmé.',                      icon: FaHandshake,    buttonText: 'En savoir plus'    },
        ].map((c, i) => (
          <StaggerItem key={i}><Card {...c} onButtonClick={() => {}} /></StaggerItem>
        ))}
      </StaggerReveal>
    </div>
  </section>
);

const SectionBureau = () => <Bureau />;

const SectionGymnase = () => (
  <section className="section bg-white">
    <div className="max-w-6xl mx-auto">
      <ScrollReveal className="mb-10">
        <span className="eyebrow mb-3 block">Nos installations</span>
        <h2 className="display-md">Le Gymnase</h2>
      </ScrollReveal>
      <ScrollReveal delay={0.1} className="rounded-2xl overflow-hidden shadow-soft mb-10">
        <iframe
          src="https://www.google.com/maps/embed?pb=!3m2!1sen!2sfr!4v1724798711302!5m2!1sen!2sfr!6m8!1m7!1sPf1bK2z2Bu_5STPxpEbdgg!2m2!1d49.02141677790688!2d2.111934957393812!3f75.09783546343078!4f0.3226142791688602!5f0.7820865974627469"
          width="100%" height="480" style={{ border: 0, display: 'block' }}
          allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
          title="Visite virtuelle du gymnase"
        />
      </ScrollReveal>
      <StaggerReveal className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          '7 terrains de badminton aux normes internationales',
          'Salle supplémentaire de 4 terrains pour les tournois',
          'Club house dédié pour se retrouver et célébrer',
          'Vestiaires modernes et spacieux',
        ].map((item, i) => (
          <StaggerItem key={i}>
            <div className="card-minimal p-4 flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-brand-800 shrink-0" />
              <span className="text-sm text-stone-700">{item}</span>
            </div>
          </StaggerItem>
        ))}
      </StaggerReveal>
    </div>
  </section>
);

const SectionReglement = () => (
  <section className="section bg-stone-50">
    <div className="max-w-6xl mx-auto">
      <ScrollReveal className="mb-10">
        <span className="eyebrow mb-3 block">Vie du club</span>
        <h2 className="display-md">Règlement Intérieur</h2>
      </ScrollReveal>
      <div className="max-w-3xl space-y-4">
        {reglementArticles.map((a, i) => (
          <ScrollReveal key={i} delay={i * 0.04}>
            <div className="card-minimal p-6">
              <h3 className="font-semibold text-brand-800 text-sm mb-3">{a.title}</h3>
              {a.text && <p className="text-sm text-stone-600 leading-relaxed">{a.text}</p>}
              {a.items && (
                <ol className="space-y-2">
                  {a.items.map((item, j) => (
                    <li key={j} className="text-sm text-stone-600 flex gap-2">
                      <span className="text-brand-800 font-semibold shrink-0">{j + 1}.</span>
                      {item}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </ScrollReveal>
        ))}
      </div>
    </div>
  </section>
);

const SECTION_COMPONENTS = {
  histoire:  <SectionHistoire  />,
  culture:   <SectionCulture   />,
  bureau:    <SectionBureau    />,
  gymnase:   <SectionGymnase   />,
  reglement: <SectionReglement />,
};

// ── Page Club ────────────────────────────────────────────────
const Club = ({ setCurrentSection, currentSection }) => {
  const { tab }    = useParams();
  const navigate   = useNavigate();
  const stickyRef  = useRef(null);   // container sticky (titre + nav mobile)
  const sectionRefs = useRef({});
  const activeRef  = useRef(null);
  const isProg     = useRef(false);

  const [active, setActive] = useState(tab || 'histoire');

  // ── Décalage = header fixe + barre sticky ───────────────────
  const scrollOffset = useCallback(() =>
    FIXED_H + (stickyRef.current?.offsetHeight ?? 0)
  , []);

  // ── Scroll vers une section ─────────────────────────────────
  const goTo = useCallback((key, behavior = 'smooth') => {
    const el = sectionRefs.current[key];
    if (!el) return;
    isProg.current = true;
    const top = el.getBoundingClientRect().top + window.scrollY - scrollOffset();
    window.scrollTo({ top, behavior });
    clearTimeout(goTo._t);
    goTo._t = setTimeout(() => { isProg.current = false; }, 900);
    // Mise à jour immédiate de l'UI et de l'URL au clic
    setActive(key);
    navigate(`/club/${key}`, { replace: true });
  }, [navigate, scrollOffset]);

  // ── Mount : scroll vers la section de l'URL ─────────────────
  useEffect(() => {
    const key = tab || 'histoire';
    if (key !== 'histoire') {
      const t = setTimeout(() => goTo(key, 'instant'), 120);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Écoute scroll → update tab actif + URL ──────────────────
  useEffect(() => {
    const onScroll = () => {
      if (isProg.current) return;
      const threshold = scrollOffset() + 40;
      const keys = tabDefs.map(t => t.key);
      let current = keys[0];
      for (const key of keys) {
        const el = sectionRefs.current[key];
        if (el && el.getBoundingClientRect().top <= threshold) current = key;
      }
      if (current !== activeRef.current) {
        activeRef.current = current;
        setActive(current);
        navigate(`/club/${current}`, { replace: true });
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [navigate, scrollOffset]);

  const meta = tabDefs.find(t => t.key === active);

  return (
    <>
      {/* ── En-tête de page ─────────────────────────────────── */}
      <PageHeader
        title="Le Club"
        subtitle="Histoire, culture, équipe dirigeante et installations du BCE"
        setCurrentSection={setCurrentSection}
        currentSection={currentSection}
      />

      {/* ── Barre sticky : titre section (+ TabNav sur mobile) ── */}
      <div
        ref={stickyRef}
        className="sticky z-30 bg-white/95 backdrop-blur-md border-b border-stone-100 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
        style={{ top: FIXED_H }}
      >
        {/* Titre de section — toujours visible */}
        <div className="max-w-6xl mx-auto px-5 md:pl-8 py-3 flex items-center gap-3 min-h-[52px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="flex items-center gap-3"
            >
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-brand-800/60">
                {meta?.eyebrow}
              </span>
              <div className="w-px h-4 bg-stone-200 shrink-0" />
              <span className="text-sm font-bold text-stone-950">
                {meta?.title}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="border-t border-stone-100">
          <div className="max-w-6xl mx-auto px-5 md:pl-8">
            <TabNav tabs={tabDefs} activeTab={active} onTabChange={goTo} />
          </div>
        </div>
      </div>

      {/* ── Sections empilées ───────────────────────────────── */}
      {tabDefs.map(({ key }) => (
        <div
          key={key}
          ref={el => { sectionRefs.current[key] = el; }}
          data-tab-key={key}
        >
          {SECTION_COMPONENTS[key]}
        </div>
      ))}
    </>
  );
};

export default Club;
